import { useMemo } from "react";
import AssigneesAvatarGroup, {
  type AssigneeAvatarItem,
} from "components/common/AssigneesAvatarGroup";
import type {
  Enquiry,
  EnquiryAssignmentSummary,
} from "features/Enquiries/types/enquiry.types";

function assigneeLabel(a: EnquiryAssignmentSummary): string {
  const u = a.user;
  const name = [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (u?.email) return u.email;
  const id = u?.id ?? a.toUserId;
  return id > 0 ? `User #${id}` : "—";
}

function assigneeUserId(a: EnquiryAssignmentSummary): number | null {
  const raw = a.user?.id ?? a.toUserId;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function dedupeKey(a: EnquiryAssignmentSummary): number {
  const uid = assigneeUserId(a);
  if (uid != null) return uid;
  const id = Number(a.id);
  return Number.isFinite(id) && id > 0 ? id : 0;
}

export default function EnquiryAssigneesAvatarGroup({
  enquiry,
  workAssignments,
  canReadDetail,
  canAssignMore,
  onOpenAssign,
}: {
  enquiry: Enquiry;
  workAssignments?: EnquiryAssignmentSummary[] | null;
  canReadDetail: boolean;
  canAssignMore: boolean;
  onOpenAssign: (e: Enquiry) => void;
}) {
  const assignees = useMemo<AssigneeAvatarItem[]>(() => {
    const list = (workAssignments ?? []).filter((a) => !a.deleted_at);
    const map = new Map<number, EnquiryAssignmentSummary>();
    for (const a of list) {
      const k = dedupeKey(a);
      if (k > 0 && !map.has(k)) map.set(k, a);
    }
    return Array.from(map.values()).map((a) => ({
      key: dedupeKey(a) || a.id,
      userId: assigneeUserId(a),
      label: assigneeLabel(a),
      avatar: a.user?.avatar,
      employee: a.user?.employee,
    }));
  }, [workAssignments]);

  return (
    <AssigneesAvatarGroup
      assignees={assignees}
      detailTo={`/enquiry-management/enquiries/${enquiry.id}`}
      canReadDetail={canReadDetail}
      canAssignMore={canAssignMore}
      onOpenAssign={() => onOpenAssign(enquiry)}
      emptyTitle="No assignees — open record to assign"
      addTitle="Assign user"
      showAddWhenLessThan={5}
    />
  );
}
